import { callEndpoint } from 'redux/global';
import { IResponse } from 'types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import {
  fetchDbDataWithCache,
  getDbCacheContextFromState,
} from 'utils/dbIndexedCache';
import { Log } from './hooks';
import { Metric } from 'pages/redux';
import { withLiveDemoSessionLog } from './liveDemoSessionLog';

export type LogSheetEntry = {
  id: number,
  message: string,
  value: number,
  date: string,
  updatedBy: string,
  updatedAsOf: string,
  sheetName: string,
}

export type BranchFile = Record<string, LogSheetEntry[]>;
export type BranchFiles = Record<string, BranchFile>

export const logTypes0 = ['Desk reservation', 'Reservation cancelled', 'Room added', 'Staff logged in',
  'Settings change',
  'CRM campaign added', 'CRM campaign removed', 'CRM campaign activated', 'CRM campaign deactivated',
  'CRM campaign changed', 'CRM campaign graphic uploaded', 'CRM campaign graphic deleted',
  'CRM discount changed', 'CRM email routing changed',
  'Online reservation',
  'Room put on hold', 'Room hold removed', 'Room renamed', 'Room floor changed', 'Room perks changed',
  'Room type changed', 'Reservation change',
  'Staff added', 'Staff removed', 'Staff change', 'Rate Plan change', 'Room deleted', 'Audit deleted',
  'Audit change', 'Audit restored', 'Meal delivered', 'Housekeeping', 'Check in', 'Desk check-in', 'Check out',
  'Room Actions', 'Key card access', 'Room charge', 'Inventory Added', 'Inventory Renamed', 'Inventory deleted',
  'Walk in', 'Walk out', 'Direct', 'Expense', 'Document',
  'Low-level favorable', 'Low-level note', 'Low-level problem', 'Report closed'] as const;

export const logKeys0 = ['Hospitality', 'Departments', 'Department entries', 'Inventory', 'Inventory Group', 'Actions', 'Show all'];

const defaultChecked = JSON.stringify([...logTypes0, ...logKeys0]);

// Former Walk-ins / Systems types now live under Actions
const roomEditLogTypes = [
  'Room put on hold', 'Room hold removed', 'Room renamed',
  'Room floor changed', 'Room perks changed', 'Room type changed',
];

const crmSettingsLogTypes = [
  'CRM campaign added', 'CRM campaign removed', 'CRM campaign activated', 'CRM campaign deactivated',
  'CRM campaign changed', 'CRM campaign graphic uploaded', 'CRM campaign graphic deleted',
  'CRM discount changed', 'CRM email routing changed',
];

const reportStandingLogTypes = [
  'Low-level favorable', 'Low-level note', 'Low-level problem',
];

const actionsTypesFromMergedGroups = [
  'Walk in', 'Walk out',
  'Rate Plan change', 'Staff added', 'Staff change', 'Staff removed',
  'Settings change', ...crmSettingsLogTypes, 'Room added',
  ...roomEditLogTypes,
  'Room deleted', 'Audit change', 'Audit deleted', 'Audit restored',
  'Inventory Added', 'Inventory Renamed', 'Inventory deleted',
  ...reportStandingLogTypes,
  'Report closed',
];

const migrateLogsChecked = (checked: string[]): string[] => {
  const next = checked.map((c) => (c === 'Branches' ? 'Departments' : c));
  // Persisted filters predate newer Actions types — keep them on when Actions/Show all already selected
  const actionsSelected = next.includes('Actions') || next.includes('Show all');
  if (actionsSelected && !next.includes('Key card access')) {
    next.push('Key card access');
  }
  if (actionsSelected && !next.includes('Desk check-in')) {
    next.push('Desk check-in');
  }
  if (actionsSelected && !next.includes('Room charge')) {
    next.push('Room charge');
  }
  if (actionsSelected) {
    actionsTypesFromMergedGroups.forEach((type) => {
      if (!next.includes(type)) next.push(type);
    });
  }
  // Old generic "Room change" filter → new per-action types, then drop the
  // leftover key so it cannot keep re-selecting those types on every save.
  if (next.includes('Room change')) {
    roomEditLogTypes.forEach((type) => {
      if (!next.includes(type)) next.push(type);
    });
  }
  if (next.includes('CRM settings change')) {
    crmSettingsLogTypes.forEach((type) => {
      if (!next.includes(type)) next.push(type);
    });
  }
  const departmentEntriesSelected = next.includes('Department entries') || next.includes('Show all');
  if (departmentEntriesSelected && !next.includes('Direct')) next.push('Direct');
  if (departmentEntriesSelected && !next.includes('Expense')) next.push('Expense');
  if (departmentEntriesSelected && !next.includes('Document')) next.push('Document');
  // Drop old group keys and the generic Room change filter (types already expanded above)
  return next.filter((c) => (
    c !== 'Walk-ins' && c !== 'Systems' && c !== 'Room change' && c !== 'CRM settings change'
  ));
};

export type LogsValueMode = 'value' | 'units';

/** /audits type column: department / inventory group / SKU. */
export type AuditsTypeAttributeMode = 'department' | 'inventory_group' | 'sku';

const normalizeStoredAuditsTypeAttributeMode = (
  mode: string | null | undefined,
): AuditsTypeAttributeMode => {
  if (mode === 'sku') return 'sku';
  if (mode === 'inventory_group') return 'inventory_group';
  // Legacy rollup → department.
  return 'department';
};

/** Bottom search-mode radios: exact tagged match (room / staff / customer / department / ota). */
export type LogSearchMatchMode = 'any' | 'room' | 'staff' | 'customer' | 'department' | 'ota';

/** Draft / per-chip audit search settings (dropdown). */
export type LogSearchOptions = {
  /** Require full-term equality (no substring / slice). */
  exactMatch: boolean,
  /** Exclude matching results. */
  negate: boolean,
  /** Skip archived inventory items, groups, and deleted departments. */
  hideArchives: boolean,
  /**
   * 2nd+ search only: match against the log results from before the previous
   * search (not the original full log list) and union those hits into the
   * last results. External filters still apply after search.
   */
  fromFullLogs: boolean,
  /** Exact tagged match; `any` = general details search. */
  matchMode: LogSearchMatchMode,
};

export const DEFAULT_LOG_SEARCH_OPTIONS: LogSearchOptions = {
  exactMatch: false,
  negate: false,
  hideArchives: false,
  fromFullLogs: false,
  matchMode: 'any',
};

export const normalizeLogSearchOptions = (
  partial?: (Partial<LogSearchOptions> & {
    /** Legacy per-kind archive flags — any one turns hideArchives on. */
    hideTypeArchives?: boolean,
    hideGroupArchives?: boolean,
    hideDepartmentArchives?: boolean,
  }) | null,
): LogSearchOptions => {
  const mode = partial?.matchMode;
  return {
    exactMatch: Boolean(partial?.exactMatch),
    negate: Boolean(partial?.negate),
    hideArchives: Boolean(
      partial?.hideArchives
      || partial?.hideTypeArchives
      || partial?.hideGroupArchives
      || partial?.hideDepartmentArchives,
    ),
    fromFullLogs: Boolean(partial?.fromFullLogs),
    matchMode: mode === 'room' || mode === 'staff' || mode === 'customer'
      || mode === 'department' || mode === 'ota'
      ? mode
      : 'any',
  };
};

/** One committed (or draft-shaped) audit search. */
export type LogSearchFilter = {
  term: string,
  options: LogSearchOptions,
};

export interface LogsState {
  logs: Log[] | null,
  nonBranchLogs: Log[] | null,
  singleLogTime: Metric | null,
  checked: string[],
  recentLog: string,
  branchFiles: BranchFiles | null,
  /** Active draft for the audits selector search input. */
  logSearch: string,
  /** Draft search options (dropdown). */
  logSearchOptions: LogSearchOptions,
  /**
   * Committed multi-search chips (max 5). Default: each narrows the previous
   * remainder. With `fromFullLogs` (2nd+): union matches from the results
   * before the previous search.
   */
  logSearches: LogSearchFilter[],
  isLoadingLogs: boolean,
  /** Associate Manager unlock: see higher-role audits (Compliance review only). */
  associateElevatedAudits: boolean,
  /**
   * Compliance → associated audits: after the audited cashier's first entry,
   * hide other users' logs. Cleared when time range is turned off or changed.
   */
  isComplianceAudit: boolean,
  /** Staff id of the Compliance-audited cashier (preferred match key). */
  complianceAuditStaffId: string | null,
  /** Username of the Compliance-audited cashier (legacy / fallback match). */
  complianceAuditUsername: string | null,
  /**
   * Temporary Audits identity from a staff report "Show logs" jump —
   * the other party's item uses their visibility. Own items stay as you.
   * Never elevates above the signed-in user's real permission.
   */
  auditsViewAs: {
    permission: number,
    staffId: string | null,
    username: string | null,
    label?: string | null,
  } | null,
  /**
   * Staff-report "Show all logs": keep only these activity_logs ids
   * (one audit set). Cleared when leaving Audits or turning Time off.
   */
  auditsOnlyLogIds: string[] | null,
  /** Report jump: first log in the clicked set (inclusive). */
  auditsStartLogId: string | null,
  /** Report jump: last log in the clicked set (inclusive). */
  auditsEndLogId: string | null,
  /** Roll up value logs by SKU/type in the Audits table. */
  auditsCombineSku: boolean,
  /** Value column mode in /audits (Compliance can jump to Units). */
  logsValueMode: LogsValueMode,
  /**
   * When a department or inventory group is selected, how temporary type
   * labels attribute rows (department / inventory_group / sku).
   */
  auditsTypeAttributeMode: AuditsTypeAttributeMode,
}

const initialChecked = migrateLogsChecked(
  JSON.parse(localStorage.getItem('logs_checked') || defaultChecked)
);
localStorage.setItem('logs_checked', JSON.stringify(initialChecked));

const LOGS_SEARCHES_KEY = 'logs_searches';
const LOGS_SEARCH_KEY = 'logs_search';
const LOGS_SEARCH_OPTIONS_KEY = 'logs_search_options';
const LOGS_SEARCH_NEGATE_KEY = 'logs_search_negate';

const parseFilterOptions = (f: any): LogSearchOptions => {
  if (f?.options && typeof f.options === 'object') {
    return normalizeLogSearchOptions(f.options);
  }
  // Legacy chips stored only `negate`.
  return normalizeLogSearchOptions({
    ...DEFAULT_LOG_SEARCH_OPTIONS,
    negate: Boolean(f?.negate),
  });
};

const parseStoredSearches = (): LogSearchFilter[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(LOGS_SEARCHES_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((f) => f && typeof f.term === 'string' && f.term.trim())
      .slice(0, 5)
      .map((f) => ({
        term: String(f.term).trim(),
        options: parseFilterOptions(f),
      }));
  } catch {
    return [];
  }
};

const initialLogSearches = parseStoredSearches();
const initialLogSearch = (() => {
  try {
    return String(localStorage.getItem(LOGS_SEARCH_KEY) || '');
  } catch {
    return '';
  }
})();
const initialLogSearchOptions = (() => {
  try {
    const raw = localStorage.getItem(LOGS_SEARCH_OPTIONS_KEY);
    if (raw) {
      return normalizeLogSearchOptions(JSON.parse(raw));
    }
    // Migrate legacy negate-only key.
    if (localStorage.getItem(LOGS_SEARCH_NEGATE_KEY) === '1') {
      return normalizeLogSearchOptions({ ...DEFAULT_LOG_SEARCH_OPTIONS, negate: true });
    }
  } catch {
    // fall through
  }
  return { ...DEFAULT_LOG_SEARCH_OPTIONS };
})();

const persistLogSearches = (searches: LogSearchFilter[]) => {
  localStorage.setItem(LOGS_SEARCHES_KEY, JSON.stringify(searches));
};

const persistLogSearch = (search: string) => {
  localStorage.setItem(LOGS_SEARCH_KEY, search);
};

const persistLogSearchOptions = (options: LogSearchOptions) => {
  localStorage.setItem(LOGS_SEARCH_OPTIONS_KEY, JSON.stringify(options));
  localStorage.setItem(LOGS_SEARCH_NEGATE_KEY, options.negate ? '1' : '0');
};

const initialState: LogsState = {
  logs: null,
  nonBranchLogs: null,
  singleLogTime: null,
  checked: initialChecked,
  recentLog: '',
  branchFiles: null,
  logSearch: initialLogSearch,
  logSearchOptions: initialLogSearchOptions,
  logSearches: initialLogSearches,
  isLoadingLogs: true,
  associateElevatedAudits: false,
  isComplianceAudit: false,
  complianceAuditStaffId: null,
  complianceAuditUsername: null,
  auditsViewAs: null,
  auditsOnlyLogIds: null,
  auditsStartLogId: null,
  auditsEndLogId: null,
  auditsCombineSku: false,
  logsValueMode: 'value',
  auditsTypeAttributeMode: 'department',
};

export const counterSlice = createSlice({
  name: 'logs',
  initialState,
  reducers: {
    setLogs: (state, action: PayloadAction<Log[] | null>) => {
      state.logs = action.payload;
    },
    setSingleLogTime: (state, action: PayloadAction<Metric | null>) => {
      state.singleLogTime = action.payload;
      if (!action.payload) {
        state.associateElevatedAudits = false;
        state.isComplianceAudit = false;
        state.complianceAuditStaffId = null;
        state.complianceAuditUsername = null;
        // Report "view as the other party" / audit-set log list only lasts for that timed jump.
        state.auditsViewAs = null;
        state.auditsOnlyLogIds = null;
        state.auditsStartLogId = null;
        state.auditsEndLogId = null;
      }
    },
    setLogsChecked: (state, action: PayloadAction<string[]>) => {
      const checked = migrateLogsChecked(action.payload);
      state.checked = checked;
      localStorage.setItem('logs_checked', JSON.stringify(checked));
    },
    setRecentLog: (state, action: PayloadAction<string>) => {
      state.recentLog = action.payload;
    },
    setBranchFiles: (state, action: PayloadAction<BranchFiles | null>) => {
      state.branchFiles = action.payload;
    },
    setNonBranchLogs: (state, action: PayloadAction<Log[] | null>) => {
      state.nonBranchLogs = action.payload;
    },
    setLogSearch: (state, action: PayloadAction<string>) => {
      state.logSearch = action.payload;
      persistLogSearch(action.payload);
    },
    setLogSearchOptions: (state, action: PayloadAction<LogSearchOptions | Partial<LogSearchOptions>>) => {
      state.logSearchOptions = normalizeLogSearchOptions({
        ...state.logSearchOptions,
        ...action.payload,
      });
      persistLogSearchOptions(state.logSearchOptions);
    },
    setLogSearches: (state, action: PayloadAction<LogSearchFilter[]>) => {
      state.logSearches = action.payload.slice(0, 5).map((f) => ({
        term: f.term,
        options: normalizeLogSearchOptions(f.options),
      }));
      persistLogSearches(state.logSearches);
    },
    setIsLoadingLogs: (state, action: PayloadAction<boolean>) => {
      state.isLoadingLogs = action.payload;
    },
    setAssociateElevatedAudits: (state, action: PayloadAction<boolean>) => {
      state.associateElevatedAudits = action.payload;
    },
    setComplianceAudit: (state, action: PayloadAction<{
      active: boolean,
      staffId?: string | null,
      username?: string | null,
    }>) => {
      const { active, staffId, username } = action.payload;
      state.isComplianceAudit = active;
      if (!active) {
        state.complianceAuditStaffId = null;
        state.complianceAuditUsername = null;
        return;
      }
      state.complianceAuditStaffId = staffId != null && String(staffId) !== ''
        ? String(staffId)
        : null;
      state.complianceAuditUsername = username?.trim() || null;
    },
    setLogsValueMode: (state, action: PayloadAction<LogsValueMode>) => {
      state.logsValueMode = action.payload === 'units' ? 'units' : 'value';
    },
    setAuditsTypeAttributeMode: (state, action: PayloadAction<AuditsTypeAttributeMode | 'rollup'>) => {
      state.auditsTypeAttributeMode = normalizeStoredAuditsTypeAttributeMode(action.payload);
    },
    setAuditsViewAs: (state, action: PayloadAction<{
      permission: number,
      staffId?: string | null,
      username?: string | null,
      label?: string | null,
    } | null>) => {
      if (!action.payload) {
        state.auditsViewAs = null;
        return;
      }
      const permission = Number(action.payload.permission);
      if (!Number.isFinite(permission) || permission < 0) {
        state.auditsViewAs = null;
        return;
      }
      state.auditsViewAs = {
        permission: Math.floor(permission),
        staffId: action.payload.staffId != null && String(action.payload.staffId)
          ? String(action.payload.staffId)
          : null,
        username: action.payload.username != null && String(action.payload.username).trim()
          ? String(action.payload.username).trim()
          : null,
        label: action.payload.label != null && String(action.payload.label).trim()
          ? String(action.payload.label).trim().slice(0, 80)
          : null,
      };
    },
    setAuditsOnlyLogIds: (state, action: PayloadAction<string[] | null>) => {
      if (!action.payload?.length) {
        state.auditsOnlyLogIds = null;
        return;
      }
      const ids = action.payload.map((id) => String(id || '').trim()).filter(Boolean);
      state.auditsOnlyLogIds = ids.length ? ids : null;
    },
    setAuditsStartEndLogIds: (state, action: PayloadAction<{
      startLogId?: string | null,
      endLogId?: string | null,
    } | null>) => {
      const start = String(action.payload?.startLogId || '').trim();
      const end = String(action.payload?.endLogId || '').trim();
      state.auditsStartLogId = start || null;
      state.auditsEndLogId = end || null;
    },
    setAuditsCombineSku: (state, action: PayloadAction<boolean>) => {
      state.auditsCombineSku = Boolean(action.payload);
    },
  },
});

export const {
  setLogs, setSingleLogTime, setLogsChecked, setRecentLog,
  setBranchFiles,
  setNonBranchLogs,
  setLogSearch,
  setLogSearchOptions,
  setLogSearches,
  setIsLoadingLogs,
  setAssociateElevatedAudits,
  setComplianceAudit,
  setLogsValueMode,
  setAuditsTypeAttributeMode,
  setAuditsViewAs,
  setAuditsOnlyLogIds,
  setAuditsStartEndLogIds,
  setAuditsCombineSku,
} = counterSlice.actions;

export const selectLogs = (state: RootState) => state.logs.logs;
export const selectNonBranchLogs = (state: RootState) => state.logs.nonBranchLogs;
export const selectSingleLogTime = (state: RootState) => state.logs.singleLogTime;
export const selectLogsChecked = (state: RootState) => state.logs.checked;
export const selectRecentLog = (state: RootState) => state.logs.recentLog;
export const selectBranchFiles = (state: RootState) => state.logs.branchFiles;
export const selectLogSearch = (state: RootState) => state.logs.logSearch;
export const selectLogSearchOptions = (state: RootState) => state.logs.logSearchOptions;
export const selectLogSearches = (state: RootState) => state.logs.logSearches;
export const selectIsLoadingLogs = (state: RootState) => state.logs.isLoadingLogs;
export const selectAssociateElevatedAudits = (state: RootState) => state.logs.associateElevatedAudits;
export const selectIsComplianceAudit = (state: RootState) => state.logs.isComplianceAudit;
export const selectComplianceAuditStaffId = (state: RootState) => state.logs.complianceAuditStaffId;
export const selectComplianceAuditUsername = (state: RootState) => state.logs.complianceAuditUsername;
export const selectLogsValueMode = (state: RootState) => state.logs.logsValueMode;
export const selectAuditsTypeAttributeMode = (state: RootState) => (
  normalizeStoredAuditsTypeAttributeMode(state.logs.auditsTypeAttributeMode)
);
export const selectAuditsViewAs = (state: RootState) => state.logs.auditsViewAs;
export const selectAuditsOnlyLogIds = (state: RootState) => state.logs.auditsOnlyLogIds;
export const selectAuditsStartLogId = (state: RootState) => state.logs.auditsStartLogId;
export const selectAuditsEndLogId = (state: RootState) => state.logs.auditsEndLogId;
export const selectAuditsCombineSku = (state: RootState) => state.logs.auditsCombineSku;
/** Report Show logs jump — pin the table until Exit. Start/end log ids are a range, not this. */
export const selectIsAuditsReportLogsFocus = (state: RootState) => Boolean(
  state.logs.auditsViewAs
  || state.logs.auditsOnlyLogIds?.length,
);

export default counterSlice.reducer;

export const getLogs = (forceRefresh = false) => async (
  dispatch: Function,
  getState: Function,
): Promise<IResponse> => {
  const ctx = getDbCacheContextFromState(getState());
  const res = await fetchDbDataWithCache(ctx, {
    table: 'logs',
    forceRefresh,
    fetchFromBe: () => dispatch(
      callEndpoint({
        api: 'getlogs',
        method: 'GET',
      }),
    ),
  });
  if (res.status === 'success' && Array.isArray(res.data)) {
    const { liveView, staffId, username, permission } = getState().global;
    res.data = withLiveDemoSessionLog(res.data, Boolean(liveView), {
      staffId,
      username,
      permission,
    });
  }
  return res;
};

export type AddBranchLog = {
  type: string,
  value: string,
  message: string,
  date: Date,
  department?: string,
  inventory_type?: string,
  units?: string | number,
  inventory_item_id?: string,
  stock_start?: string | number,
  stock_left?: string | number,
}

export const addBranchLog = (logEntry: AddBranchLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addbranchlog',
      method: 'POST',
      body: { ...logEntry }
    }),
  );
  return res;
};

export const addBranchLogs = (logs: AddBranchLog[]) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addbranchlogs',
      method: 'POST',
      body: { logs }
    }),
  );
  return res;
};

type DeleteLog = {
  dId: string,
  type: string,
  value: string,
  skipFeedback?: boolean,
}

export const deleteLog = (logToDelete: DeleteLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletelog',
      method: 'POST',
      body: { ...logToDelete }
    }),
  );
  return res;
};

type RestoreLog = {
  auditId: string,
}

export const restoreLog = (logToRestore: RestoreLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'restorelog',
      method: 'POST',
      body: { ...logToRestore }
    }),
  );
  return res;
};

type EditLog = {
  editId: string,
  addLogMessage?: string,
  value?: string,
  message?: string,
  skipFeedback?: boolean,
}

export const editLog = (logToEdit: EditLog) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'editlog',
      method: 'POST',
      body: { ...logToEdit }
    }),
  );
  return res;
};

export const addLog = (log: Omit<Omit<Log, 'id'>, 'date'>) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addlog',
      method: 'POST',
      body: { log }
    }),
  );
  return res;
};

export const getBranchFiles = () => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'getbranchfiles',
      method: 'GET',
    }),
  );
  return res;
};

export const getBranchFile = (branch: string, sheetName: string) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'getbranchfile',
      method: 'POST',
      body: { branch, sheetName }
    }),
  );
  return res;
};

export const addBranchFile = (branchFile: BranchFiles) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addbranchfile',
      method: 'POST',
      body: { branchFile }
    }),
  );
  return res;
};

/** Storage check + signed GCS PUT URL for large extracts. */
export const prepareBranchFileUpload = (
  branch: string,
  sheetName: string,
  bytes: number,
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'preparebranchfileupload',
      method: 'POST',
      body: { branch, sheetName, bytes },
    }),
  );
  return res;
};

export const deleteBranchFile = (branch: string, sheetName: string) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletebranchfile',
      method: 'POST',
      body: { branch, sheetName }
    }),
  );
  return res;
};

export const deleteBranchFiles = (branch: string) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletebranchfiles',
      method: 'POST',
      body: { branch }
    }),
  );
  return res;
};

type DeleteLogType = {
  type: string,
}

export const deleteLogType = (logTypeToDelete: DeleteLogType) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletelogtype',
      method: 'POST',
      body: { ...logTypeToDelete }
    }),
  );
  return res;
};

export type RenameDepartmentInLogs = {
  from: string,
  to: string,
}

export const renameDepartmentInLogs = (payload: RenameDepartmentInLogs) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'renamedepartmentinlogs',
      method: 'POST',
      body: { ...payload },
    }),
  );
  return res;
};
